import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import {
  Home,
  Wallet,
  Receipt,
  PieChart,
  BarChart3,
  Target,
  RefreshCw,
  Banknote,
  Upload,
  Settings,
  Bell,
  Shield,
  LogOut,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { useLogout } from '../../features/auth/api/useAuth';
import { useNotificationContext } from '../../features/notifications/context/NotificationProvider';
import { ActionCenterDrawer } from '../../features/notifications/components/ActionCenterDrawer';
import { FinLabLogo } from '../../shared/components/FinLabLogo';

type NavItem = {
  path: string;
  icon: typeof Home;
  labelKey: string;
};

const primaryNav: readonly NavItem[] = [
  { path: '/dashboard', icon: Home, labelKey: 'nav.home' },
  { path: '/finance', icon: Wallet, labelKey: 'nav.finance' },
  { path: '/transactions', icon: Receipt, labelKey: 'nav.transactions' },
  { path: '/budgets', icon: PieChart, labelKey: 'nav.budgets' },
  { path: '/reports', icon: BarChart3, labelKey: 'nav.insights' },
] as const;

const moreNav: readonly NavItem[] = [
  { path: '/savings', icon: Target, labelKey: 'nav.goals' },
  { path: '/recurring', icon: RefreshCw, labelKey: 'nav.recurring' },
  { path: '/debts', icon: Banknote, labelKey: 'nav.debts' },
  { path: '/import', icon: Upload, labelKey: 'nav.import' },
] as const;

export function Sidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logoutMutation = useLogout();
  const { actionCount } = useNotificationContext();
  const [showDrawer, setShowDrawer] = useState(false);

  function isActive(path: string): boolean {
    if (path === '/dashboard') {
      return location.pathname === path || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  }

  function handleLogout() {
    logoutMutation.mutate();
  }

  function renderItem({ path, icon: Icon, labelKey }: NavItem) {
    const active = isActive(path);
    return (
      <Link
        key={path}
        to={path}
        className={[
          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
          active
            ? 'bg-brand-purple-600 text-white shadow-[0_4px_12px_rgba(124,58,237,0.4)]'
            : 'text-gray-300 hover:bg-white/5 hover:text-white',
        ].join(' ')}
      >
        <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
        <span className="truncate">{t(labelKey)}</span>
      </Link>
    );
  }

  const isAdmin = user?.role === 'ADMIN';
  const adminActive = location.pathname.startsWith('/admin');
  const settingsActive = location.pathname.startsWith('/settings');

  return (
    <>
      <aside className="hidden md:flex md:flex-col fixed inset-y-0 left-0 w-60 bg-gray-900 z-30">
        {/* Brand */}
        <Link to="/dashboard" className="flex items-center px-5 pt-5 pb-6 shrink-0">
          <FinLabLogo variant="dark" size="md" />
        </Link>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 pb-3">
          <div className="text-[11px] uppercase tracking-[0.08em] text-gray-500 font-semibold px-3 pt-2 pb-1.5">
            {t('nav.primary')}
          </div>
          <div className="space-y-0.5">
            {primaryNav.map(renderItem)}
          </div>
          <div className="text-[11px] uppercase tracking-[0.08em] text-gray-500 font-semibold px-3 pt-4 pb-1.5">
            {t('nav.more')}
          </div>
          <div className="space-y-0.5">
            {moreNav.map(renderItem)}
          </div>
        </nav>

        {/* Bottom section */}
        <div className="shrink-0 border-t border-gray-800 px-3 py-3 space-y-0.5">
          <button
            onClick={() => setShowDrawer(true)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors relative"
          >
            <Bell className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
            <span>{t('nav.actionCenter')}</span>
            {actionCount > 0 && (
              <span className="ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">
                {actionCount > 9 ? '9+' : actionCount}
              </span>
            )}
          </button>

          {isAdmin && (
            <Link
              to="/admin"
              className={[
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                adminActive
                  ? 'bg-brand-purple-600 text-white'
                  : 'text-gray-300 hover:bg-white/5 hover:text-white',
              ].join(' ')}
            >
              <Shield className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
              <span>{t('nav.admin')}</span>
            </Link>
          )}

          <Link
            to="/settings"
            className={[
              'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              settingsActive
                ? 'bg-brand-purple-600 text-white'
                : 'text-gray-300 hover:bg-white/5 hover:text-white',
            ].join(' ')}
          >
            <Settings className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
            <span>{t('nav.settings')}</span>
          </Link>

          <div className="pt-2 mt-1 border-t border-gray-800 flex items-center gap-3 px-2">
            <div className="w-9 h-9 rounded-full bg-brand-purple-600 flex items-center justify-center text-white text-sm font-semibold">
              {user?.name?.charAt(0)?.toUpperCase() ?? '?'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white truncate">
                {user?.name ?? '—'}
              </div>
              <div className="text-xs text-gray-400 truncate">
                {user?.email ?? ''}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-white/5 rounded-md transition-colors"
              aria-label={t('nav.logout')}
            >
              <LogOut className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </aside>
      <ActionCenterDrawer isOpen={showDrawer} onClose={() => setShowDrawer(false)} />
    </>
  );
}
